import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganizationConfigurationTabs } from '@constants/administration/organization/organization-configuration-tabs';
import { EnvironmentConfig } from '@environments/environment';
import { Branding } from '@models/adminstration/organization/branding/branding';
import { OrganizationConstant } from '@models/adminstration/organization/constants/organization-constant';
import { PortalGeneralSetting } from '@models/adminstration/organization/constants/portal-general-setting';
import { MailSettings } from '@models/adminstration/organization/mail/mail-settings';
import { SupportContact } from '@models/adminstration/organization/support-contact/support-contact';
import { Machine } from '@models/machine/machine';
import { User } from '@models/user/user';
import { TranslateService } from '@ngx-translate/core';
import { utob } from '@prototypes/global.functions';
import { BrandingService } from '@services/administration/organization/configuration/branding/branding.service';
import { ConstantsService } from '@services/administration/organization/configuration/constants/constants.service';
import { ContactsService } from '@services/administration/organization/configuration/contacts/contacts.service';
import { MailTemplateService } from '@services/administration/organization/configuration/mail-template/mail-template.service';
import { OrganizationService } from '@services/administration/organization/organization.service';
import { LoadingIndicatorService } from '@services/loading-indicator/loading-indicator.service';
import { NotificationService } from '@services/notification/notification.service';
import { Observable, Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'scitis-admin-organization-configuration',
    templateUrl: './configuration.component.html',
    styleUrls: ['./configuration.component.scss'],
    standalone: false
})
export class ConfigurationComponent implements OnInit {

  private CURRENT_PAGE_NAME = 'OrganizationConfiguration';

  organizationId: number = -1;
  parentOrganizationId: number | undefined;
  organizationName: string = '';
  isAdminRole: boolean = false;
  isSuperAdminRole: boolean = false;
  searchTerm: string = '';
  show: string = OrganizationConfigurationTabs.MACHINES;
  ConfigurationTabs = OrganizationConfigurationTabs;
  organizationMachines: Machine[] | undefined;
  organizationUsers: User[] | undefined;
  organizationSupportContacts: SupportContact[] | undefined;
  organizationConstants: OrganizationConstant[] | undefined;
  portalSettings: PortalGeneralSetting[] | undefined;
  organizationMailSettings: MailSettings;
  isWhiteLabelingEnabled: boolean;
  brandingIsNotLoaded: boolean = true;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private activatedRoute: ActivatedRoute,
              private notification: NotificationService,
              private translation: TranslateService,
              private loadingIndicator: LoadingIndicatorService,
              private organizationService: OrganizationService,
              private contactsService: ContactsService,
              private constantsService: ConstantsService,
              private brandingService: BrandingService,
              private router: Router,
              private mailTemplateService: MailTemplateService) {
    this.searchTerm = this.router.currentNavigation()?.extras.state?.search;
    this.organizationMailSettings = new MailSettings();
    this.isWhiteLabelingEnabled = EnvironmentConfig.PortalSettings.whiteLabelingEnabled;
    this.getOrganizationDetailsFromUrl();
  }

  private getOrganizationDetailsFromUrl(): void {
    const details = this.activatedRoute.snapshot.paramMap.get('details');

    if (details) {
      const parsed = JSON.parse(utob(details));
      this.organizationName = parsed.name;
      this.organizationId = parsed.id;
      this.parentOrganizationId = parsed.parentOrganizationId;
      this.isAdminRole = parsed.isAdminRole;
      this.isSuperAdminRole = parsed.isSuperAdminRole;
    }
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(p => {
      this.show = p.page ?? this.ConfigurationTabs.MACHINES;
      this.loadTabData(this.show);
    });
  }

  private loadTabData(tab: string) {
    switch (tab) {
      case this.ConfigurationTabs.MACHINES:
        this.onMachinesClick();
        break;
      case this.ConfigurationTabs.USERS:
        this.onUsersClick();
        break;
      case this.ConfigurationTabs.CONTACTS:
        this.onSupportContactsClick();
        break;
      case this.ConfigurationTabs.CONSTANTS:
        this.onOrganizationConstantsClick();
        break;
      case this.ConfigurationTabs.SETTINGS:
        this.onSettingsClick();
        break;
      case this.ConfigurationTabs.BRANDING:
        this.onBrandingClick();
        break;
      case this.ConfigurationTabs.MAIL:
        this.onMailTemplatesClick();
        break;
      default:
        this.onMachinesClick();
        break;
    }
  }

  get organizationBranding () {
    return this.brandingService.organizationBranding;
  }
  get parentOrganizationBranding () {
    return this.brandingService.parentOrganizationBranding;
  }

  onNavChange(event: any) {
    this.show = event.nextId;
    this.router.navigate([], { relativeTo: this.activatedRoute, queryParams: { page: this.show } });
  }

  onMachinesClick() {
    this.show = OrganizationConfigurationTabs.MACHINES;
    if (this.organizationMachines !== undefined) {
      return;
    }
    this.loadingIndicator.show(this.translation.instant('Loading organization machines'));

    this.organizationService.listMachines(this.organizationId).pipe(takeUntil(this.destroy$)).subscribe((success) => {
      this.organizationMachines = success;
      this.loadingIndicator.hide();
    }, (error) => {
      const message = this.translation.instant('Error getting machines for organization: ') + this.organizationName + '(' + this.organizationId + ').';
      this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
      this.organizationMachines = new Array<Machine>();
      this.loadingIndicator.hide();
    });

  }

  onUsersClick() {
    this.show = OrganizationConfigurationTabs.USERS;
    if (this.organizationUsers !== undefined) {
      return;
    }

    this.loadingIndicator.show(this.translation.instant('Loading organization users'));

    this.organizationService.listUsers(this.organizationId).pipe(takeUntil(this.destroy$)).subscribe((success) => {
      this.organizationUsers = success;
      this.loadingIndicator.hide();
    }, (error) => {
      const message = this.translation.instant('Error getting users for organization: ') + this.organizationName + '(' + this.organizationId + ').';
      this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
      this.organizationUsers = new Array<User>();
      this.loadingIndicator.hide();
    });

  }

  onSupportContactsClick() {
    this.show = OrganizationConfigurationTabs.CONTACTS;
    if (this.organizationSupportContacts !== undefined) {
      return;
    }

    this.loadingIndicator.show(this.translation.instant('Loading organization support contacts'));
    this.contactsService.listContacts(this.organizationId).pipe(takeUntil(this.destroy$)).subscribe((success) => {
      this.organizationSupportContacts = success;
      this.loadingIndicator.hide();
    }, (error) => {
      const message = this.translation.instant('Error getting support contacts for organization: ') + this.organizationName + '(' + this.organizationId + ').';
      this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
      this.organizationSupportContacts = new Array<SupportContact>();
      this.loadingIndicator.hide();
    });

  }

  onOrganizationConstantsClick() {
    this.show = OrganizationConfigurationTabs.CONSTANTS;
    if (this.organizationConstants !== undefined) {
      return;
    }
    this.loadingIndicator.show(this.translation.instant('Loading organization specific constants'));
    const constantsSubscription = this.constantsService.listConstants(this.organizationId);
    constantsSubscription.pipe(takeUntil(this.destroy$)).subscribe((success) => {
      this.organizationConstants = success;
    }, (error) => {
      console.error('Error loading organization constants', JSON.stringify(error));
      this.organizationConstants = new Array<OrganizationConstant>();
    });

    const portalSettingsSubscription = this.constantsService.listPortalSettings(this.organizationId);
    portalSettingsSubscription.pipe(takeUntil(this.destroy$)).subscribe((success) => {
      this.portalSettings = success;
    }, (error) => {
      console.error('Error loading portal wide settings', JSON.stringify(error));
      this.portalSettings = new Array<PortalGeneralSetting>();
    });

    forkJoin([constantsSubscription, portalSettingsSubscription]).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loadingIndicator.hide();
    }, () => {
      this.loadingIndicator.hide();
    });

  }

  onSettingsClick(): void {
    this.show = OrganizationConfigurationTabs.SETTINGS;
  }

  onBrandingClick() {
    this.show = OrganizationConfigurationTabs.BRANDING;
    this.loadingIndicator.show(this.translation.instant('Loading organization specific design'));

    if (this.organizationId === -1) {
      this.brandingService.organizationBranding = new Branding();
      this.brandingService.parentOrganizationBranding = new Branding();
      this.loadingIndicator.hide();
      return;
    }
    const brandingSubscriptions: Observable<any>[] = new Array<Observable<any>>();
    brandingSubscriptions.push(this.brandingService.getOrganizationBranding(this.organizationId));

    if (this.parentOrganizationId != undefined && this.parentOrganizationId != -1) {
      brandingSubscriptions.push(this.brandingService.getOrganizationBranding(this.parentOrganizationId));
    }

    forkJoin(brandingSubscriptions).pipe(takeUntil(this.destroy$)).subscribe(async (results) => {
      const organizationBranding = results[0] || new Branding();
      await this.brandingService.initializeBranding(organizationBranding, this.organizationId);

      if (results.length > 1) {
        this.brandingService.parentOrganizationBranding = results[1] || new Branding();
      } else {
        this.brandingService.parentOrganizationBranding = new Branding();
      }

      this.brandingIsNotLoaded = false;
      this.loadingIndicator.hide();
    }, (error) => {
      this.brandingService.organizationBranding = new Branding();
      this.brandingService.parentOrganizationBranding = new Branding();
      this.brandingIsNotLoaded = false;
      this.loadingIndicator.hide();
      console.error(`${this.CURRENT_PAGE_NAME}. There was an error in retrieving organization branding.`, error);
    });

  }

  onMailTemplatesClick() {
    this.show = OrganizationConfigurationTabs.MAIL;
    if (this.organizationMailSettings !== undefined && this.organizationMailSettings.id !== -1) {
      return;
    }
    this.loadingIndicator.show(this.translation.instant('Loading organization eMail templates'));
    this.mailTemplateService.getTemplates(this.organizationId).pipe(takeUntil(this.destroy$)).subscribe((success) => {
      this.organizationMailSettings = success;
      this.loadingIndicator.hide();
    }, (error) => {
      this.loadingIndicator.hide();
      this.organizationMailSettings = new MailSettings();
      this.notification.error(
          this.translation.instant('Error getting mail templates for organization: ') + this.organizationName + '(' + this.organizationId + ').',
          error, this.CURRENT_PAGE_NAME, true);
    });
  }

}
